import { useRef, useState, useCallback } from 'react'
import { CheckIcon, ChevronDoubleRightIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface Props {
  // Resolves when the acknowledgement has been recorded server-side.
  onConfirm: () => Promise<void>
}

type Stage = 'idle' | 'confirming' | 'submitting' | 'done'

// Fraction of the track the handle must reach to count as confirmed.
const CONFIRM_THRESHOLD = 0.92
const HANDLE_PX = 44

// Two-stage acknowledge control: a resting checkmark → click reveals a
// slide-to-confirm track → dragging the handle to the end records the ack.
// Deliberate by design: a drag can't happen by accident the way a click can.
const AcknowledgeSlider = ({ onConfirm }: Props) => {
  const [stage, setStage] = useState<Stage>('idle')
  const [progress, setProgress] = useState(0) // 0..1
  const progressRef = useRef(0) // latest progress, read on pointer-up without stale closure
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const updateProgress = (value: number) => {
    progressRef.current = value
    setProgress(value)
  }

  const submit = useCallback(async () => {
    setStage('submitting')
    updateProgress(1)
    try {
      await onConfirm()
      setStage('done')
    } catch {
      // Failed — reset so the user can retry; the modal surfaces the error.
      setStage('confirming')
      updateProgress(0)
    }
  }, [onConfirm])

  const moveTo = useCallback((clientX: number) => {
    const track = trackRef.current
    if (!track) return
    const rect = track.getBoundingClientRect()
    const usable = rect.width - HANDLE_PX
    const x = Math.min(Math.max(clientX - rect.left - HANDLE_PX / 2, 0), usable)
    updateProgress(usable > 0 ? x / usable : 0)
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    if (stage !== 'confirming') return
    draggingRef.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    moveTo(e.clientX)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    moveTo(e.clientX)
  }

  const onPointerUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    if (progressRef.current >= CONFIRM_THRESHOLD) {
      void submit() // submit() sets progress to 1
    } else {
      updateProgress(0) // snap back
    }
  }

  if (stage === 'idle') {
    return (
      <button
        type="button"
        onClick={() => setStage('confirming')}
        className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 transition-colors"
      >
        <CheckIcon className="h-5 w-5" />
        Acknowledge
      </button>
    )
  }

  if (stage === 'done') {
    return (
      <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
        <CheckIcon className="h-5 w-5" />
        Acknowledged
      </span>
    )
  }

  const submitting = stage === 'submitting'
  const pct = Math.round(progress * 100)

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-600">Are you sure? Slide to acknowledge.</p>
        {!submitting && (
          <button
            type="button"
            onClick={() => { setStage('idle'); setProgress(0) }}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cancel"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      <div
        ref={trackRef}
        className="relative h-11 w-full max-w-sm select-none overflow-hidden rounded-full bg-gray-100"
      >
        {/* Filled trail */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-emerald-400/70"
          style={{ width: `${pct}%` }}
        />
        {/* Hint label */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-medium text-gray-500">
          {submitting ? 'Acknowledging…' : 'Slide to acknowledge'}
        </span>
        {/* Handle — moves from 0 to (track width − handle width) as pct goes 0→100 */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          role="slider"
          aria-label="Slide to acknowledge"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          tabIndex={0}
          className={`absolute top-0 flex items-center justify-center rounded-full bg-emerald-500 text-white shadow ${submitting ? '' : 'cursor-grab active:cursor-grabbing'}`}
          style={{
            height: HANDLE_PX,
            width: HANDLE_PX,
            left: `calc(${pct}% - ${(pct / 100) * HANDLE_PX}px)`,
            transition: draggingRef.current ? 'none' : 'left 150ms ease-out',
          }}
        >
          <ChevronDoubleRightIcon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}

export default AcknowledgeSlider
